import type { ReactElement } from "react";
import type { LamaMaturityTrendOutput } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_LIVE_WAVE_NOTE,
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
  lamaScoreToPercent,
} from "../lama-report-presentation.js";
import { LamaMaturityTrendChart } from "./TrendChart.js";

/**
 * LAMA Maturity Trend — overall maturity by wave. Data-blind: the input is
 * the `lama/maturity-trend@1` dataset output's `points`.
 *
 * A `live` point is the OPEN wave's running recomputation, not a frozen
 * closed-wave value (§13.4); the note says so, because the chart cannot.
 */
export function LamaMaturityTrendWidget({
  data,
}: WidgetBodyProps<LamaMaturityTrendOutput>): ReactElement {
  if (data.suppressed) {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">
          {LAMA_SUPPRESSED_TITLE}
        </p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {LAMA_SUPPRESSED_BODY}
        </p>
      </div>
    );
  }
  if (data.points.length === 0) {
    return (
      <p className="text-sm text-pb-text-muted">
        No maturity trend available — wave values will appear when report
        snapshots are available.
      </p>
    );
  }
  const hasLivePoint = data.points.some((point) => point.live);
  return (
    <div>
      {/* Percent at display (D8): the dataset's 0–5 points, drawn on 0–100.
          `date` rides along for the tooltip — the wire has carried it all
          along and nothing showed it, so the chart named waves without ever
          saying when they ran. */}
      <LamaMaturityTrendChart
        data={data.points.map(({ label, overall, date }) => ({
          label,
          overall: lamaScoreToPercent(overall),
          date,
        }))}
      />
      {hasLivePoint ? (
        <p className="mt-2 text-xs text-pb-text-muted">{LAMA_LIVE_WAVE_NOTE}</p>
      ) : null}
    </div>
  );
}
